import { CATALOG, presetFor } from "../avatar/catalog";
import { avatarPartsReady, sheetDataURL } from "../avatar/composer";
import { ASSETS, AVATAR_FRAME } from "../config";
import type { PublicUser } from "../types";
import { GestureMenu } from "./GestureMenu";

/** Escala do avatar em miniatura no botao de perfil (frame de 16x24). */
const PROFILE_THUMB_SCALE = 1;

/** Limiares de latencia (ms) para o ponto colorido do ping. */
const LATENCY_GOOD_MAX = 100;
const LATENCY_WARN_MAX = 250;

/** Controla o HUD em DOM sobreposto ao canvas do Phaser. */
export class Hud {
  private readonly root = requireElement<HTMLDivElement>("hud");
  private readonly nameEl = requireElement<HTMLSpanElement>("hud-name");
  private readonly onlineEl = requireElement<HTMLSpanElement>("hud-online");
  private readonly latencyDotEl = requireElement<HTMLSpanElement>("hud-latency-dot");
  private readonly latencyValueEl = requireElement<HTMLSpanElement>("hud-latency-value");
  private readonly leaveBtn = requireElement<HTMLButtonElement>("hud-leave");
  private readonly hintEl = requireElement<HTMLDivElement>("hud-hint");
  private readonly log = requireElement<HTMLUListElement>("chat-log");
  private readonly form = requireElement<HTMLFormElement>("chat-form");
  private readonly input = requireElement<HTMLInputElement>("chat-input");
  private readonly closeBtn = requireElement<HTMLButtonElement>("chat-close");
  private readonly badgeEl = requireElement<HTMLSpanElement>("chat-badge");
  private readonly profileBtn = requireElement<HTMLButtonElement>("hud-profile");
  private readonly profileAvatarEl = requireElement<HTMLSpanElement>("hud-profile-avatar");
  private readonly profileNameEl = requireElement<HTMLSpanElement>("hud-profile-name");
  private readonly zoomOutBtn = requireElement<HTMLButtonElement>("hud-zoom-out");
  private readonly zoomInBtn = requireElement<HTMLButtonElement>("hud-zoom-in");
  private readonly zoomValueEl = requireElement<HTMLSpanElement>("hud-zoom-value");
  private readonly modBtn = requireElement<HTMLButtonElement>("hud-mod");
  private readonly modBadge = requireElement<HTMLSpanElement>("hud-mod-badge");
  private readonly playersBtn = requireElement<HTMLButtonElement>("hud-players");
  private readonly bannerEl = requireElement<HTMLDivElement>("hud-banner");
  private bannerTimer: number | null = null;

  /** Seletor de gestos (botao no canto + grade). */
  readonly gestures = new GestureMenu();

  private currentHint = "";
  /** Contador de mensagens perdidas com o chat fechado no toque (o desktop sempre mostra o log). */
  private unread = 0;
  /** O log estava no fim antes da ultima mudanca de tela (ver fitChatToViewport). */
  private logAtBottom = true;
  private static readonly UNREAD_MAX = 99;

  /** A cena diz se o Enter pode abrir o chat agora (nao com um overlay aberto). */
  canChat: () => boolean = () => true;

  constructor() {
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      const text = this.input.value.trim();
      this.input.value = "";
      this.input.blur();
      this.closeChat();
      if (text) this.chatHandler?.(text);
    });

    this.input.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.input.value = "";
        this.input.blur();
        this.closeChat();
      }
      // Impede que o Phaser (que escuta na window) receba o que esta sendo digitado.
      event.stopPropagation();
    });

    this.input.addEventListener("focus", () => this.focusHandler?.(true));
    this.input.addEventListener("blur", () => {
      this.focusHandler?.(false);
      this.closeChat();
    });
    this.closeBtn.addEventListener("click", () => {
      this.input.value = "";
      this.input.blur();
      this.closeChat();
    });

    // Enter abre o chat de qualquer lugar do HUD. Fica aqui, e nao no teclado
    // do Phaser, para valer tambem com o foco num botao: em captura e com
    // preventDefault, o botao focado nao chega a receber o Enter.
    window.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Enter" || event.ctrlKey || event.altKey || event.metaKey || event.isComposing) return;
        if (this.root.classList.contains("hidden") || this.input.disabled || !this.canChat()) return;
        const target = event.target as HTMLElement | null;
        if (target?.closest("input, textarea, select, [contenteditable]")) return;
        event.preventDefault();
        this.gestures.close();
        this.focusChat();
      },
      true,
    );

    // No toque o chat ocupa a tela toda: acompanha o pedaco que sobra acima do
    // teclado virtual (o iOS ignora o interactive-widget e empurra a pagina).
    window.visualViewport?.addEventListener("resize", () => this.fitChatToViewport(false));
    window.visualViewport?.addEventListener("scroll", () => this.fitChatToViewport(false));
    // Encolher o log (teclado subindo) nao dispara scroll, entao o valor
    // continua o de antes da mudanca: e ele que diz se o jogador lia o fim.
    this.log.addEventListener("scroll", () => {
      this.logAtBottom = this.log.scrollTop + this.log.clientHeight >= this.log.scrollHeight - 4;
    });

    this.leaveBtn.addEventListener("click", () => this.leaveHandler?.());
    this.profileBtn.addEventListener("click", () => this.profileHandler?.());
    this.modBtn.addEventListener("click", () => {
      this.modBtn.blur();
      this.modHandler?.();
    });
    this.playersBtn.addEventListener("click", () => {
      this.playersBtn.blur();
      this.playersHandler?.();
    });
    this.bannerEl.addEventListener("click", () => this.hideBanner());
    // blur: com o foco no botao, Espaco/Enter (interagir, chat) o clicariam de novo
    this.zoomOutBtn.addEventListener("click", () => {
      this.zoomOutBtn.blur();
      this.zoomHandler?.(-1);
    });
    this.zoomInBtn.addEventListener("click", () => {
      this.zoomInBtn.blur();
      this.zoomHandler?.(1);
    });
  }

  private chatHandler?: (text: string) => void;
  private focusHandler?: (focused: boolean) => void;
  private leaveHandler?: () => void;
  private profileHandler?: () => void;
  private zoomHandler?: (step: number) => void;
  private modHandler?: () => void;
  private playersHandler?: () => void;

  onChat(handler: (text: string) => void): void {
    this.chatHandler = handler;
  }

  onChatFocusChange(handler: (focused: boolean) => void): void {
    this.focusHandler = handler;
  }

  onLeave(handler: () => void): void {
    this.leaveHandler = handler;
  }

  onProfileClick(handler: () => void): void {
    this.profileHandler = handler;
  }

  /** Botao de moderacao (so aparece para moderadores e admins, ver setStaff). */
  onModClick(handler: () => void): void {
    this.modHandler = handler;
  }

  setStaff(staff: boolean): void {
    this.modBtn.classList.toggle("hidden", !staff);
  }

  /** Selo com as denuncias abertas no botao de moderacao. */
  setOpenReports(count: number): void {
    this.modBadge.textContent = count > 99 ? "99+" : String(count);
    this.modBadge.classList.toggle("hidden", count <= 0);
    this.modBtn.title = count > 0 ? `Moderação: ${count} denúncia(s) aberta(s)` : "Moderação: jogadores online";
  }

  /** Lista de jogadores (com denunciar): so para quem tem conta. */
  onPlayersClick(handler: () => void): void {
    this.playersHandler = handler;
  }

  setPlayersAvailable(available: boolean): void {
    this.playersBtn.classList.toggle("hidden", !available);
  }

  /**
   * Faixa em destaque no topo para avisos da moderacao e anuncios: no toque o
   * log do chat fica escondido, e um aviso desses nao pode passar batido.
   * Some sozinha depois de alguns segundos ou com um toque.
   */
  showBanner(text: string, level: "info" | "warn" | "error" = "warn"): void {
    this.bannerEl.textContent = text;
    this.bannerEl.className = level;
    if (this.bannerTimer !== null) window.clearTimeout(this.bannerTimer);
    this.bannerTimer = window.setTimeout(() => this.hideBanner(), 10000);
  }

  private hideBanner(): void {
    this.bannerEl.classList.add("hidden");
    if (this.bannerTimer !== null) window.clearTimeout(this.bannerTimer);
    this.bannerTimer = null;
  }

  /** Botoes de zoom: recebe -1 (afastar) ou +1 (aproximar). */
  onZoom(handler: (step: number) => void): void {
    this.zoomHandler = handler;
  }

  /** Mostra o zoom atual e desliga o botao que passaria do limite. */
  setZoom(zoom: number, min: number, max: number): void {
    this.zoomValueEl.textContent = `${zoom}x`;
    this.zoomOutBtn.disabled = zoom <= min;
    this.zoomInBtn.disabled = zoom >= max;
  }

  /**
   * Fantasmas nao tem conta: passe null pra esconder o botao de perfil.
   * Jogadores saem pelo menu de perfil, entao o botao "Sair" do topo fica
   * reservado para fantasmas (que nao tem perfil pra abrir).
   */
  setProfile(user: PublicUser | null): void {
    this.leaveBtn.classList.toggle("hidden", !!user);
    if (!user) {
      this.profileBtn.classList.add("hidden");
      return;
    }
    this.profileBtn.classList.remove("hidden");
    this.profileNameEl.textContent = user.displayName;
    this.profileAvatarEl.style.width = `${AVATAR_FRAME.width * PROFILE_THUMB_SCALE}px`;
    this.profileAvatarEl.style.height = `${AVATAR_FRAME.height * PROFILE_THUMB_SCALE}px`;
    this.profileAvatarEl.style.backgroundImage = avatarPartsReady()
      ? `url(${sheetDataURL(user.appearance ?? presetFor(user.skin))})`
      : `url(${ASSETS.avatar(user.skin)})`;
    // altura "auto": vale para qualquer numero de linhas do spritesheet
    this.profileAvatarEl.style.backgroundSize = `${AVATAR_FRAME.width * CATALOG.frame.cols * PROFILE_THUMB_SCALE}px auto`;
  }

  show(): void {
    this.root.classList.remove("hidden");
  }

  hide(): void {
    this.root.classList.add("hidden");
    this.hideBanner();
    this.closeChat();
    this.gestures.close();
  }

  /**
   * Abre o chat (Enter no teclado, botao "Falar" no toque). No toque o campo
   * so existe enquanto esta aberto, para nao ocupar a tela nem abrir o teclado
   * virtual sem querer.
   */
  focusChat(): void {
    if (this.input.disabled) return;
    document.body.classList.add("chat-open");
    this.input.focus();
    this.setUnread(0);
    // No toque o log estava escondido: o scroll so pega depois de aparecer.
    this.fitChatToViewport(true);
  }

  /**
   * Encaixa o chat na area visivel. So volta o log para o fim ao abrir ou se o
   * jogador ja estava no fim: quem rolou para ler o historico nao e puxado de volta.
   */
  private fitChatToViewport(toBottom: boolean): void {
    if (!document.body.classList.contains("chat-open")) return;
    const viewport = window.visualViewport;
    if (viewport) {
      const style = document.documentElement.style;
      style.setProperty("--chat-top", `${viewport.offsetTop}px`);
      style.setProperty("--chat-height", `${viewport.height}px`);
    }
    if (toBottom || this.logAtBottom) this.log.scrollTop = this.log.scrollHeight;
  }

  private closeChat(): void {
    document.body.classList.remove("chat-open");
  }

  get chatFocused(): boolean {
    return document.activeElement === this.input;
  }

  setIdentity(name: string, ghost: boolean): void {
    // Fantasmas nao tem conta/avatar: mostram o nome simples. Jogadores usam
    // o botao de perfil (avatar + nome) no lugar, entao o nome simples some.
    this.nameEl.classList.toggle("hidden", !ghost);
    this.nameEl.textContent = ghost ? "Fantasma" : name;
    this.nameEl.title = ghost ? "Visitante anônimo: você observa, mas não interage" : name;

    this.input.disabled = ghost;
    this.gestures.setAvailable(!ghost);
    this.input.placeholder = ghost
      ? "Fantasmas não conversam — crie uma conta para participar"
      : "Enter para conversar…";
  }

  setOnline(count: number): void {
    this.onlineEl.textContent = `${count} online`;
  }

  setLatency(ms: number): void {
    this.latencyValueEl.textContent = `${Math.round(ms)} ms`;

    const level = ms <= LATENCY_GOOD_MAX ? "good" : ms <= LATENCY_WARN_MAX ? "warn" : "bad";
    this.latencyDotEl.classList.remove("good", "warn", "bad");
    this.latencyDotEl.classList.add(level);
  }

  setHint(text: string | null): void {
    if (text === this.currentHint) return;
    this.currentHint = text ?? "";

    if (!text) {
      this.hintEl.classList.add("hidden");
      this.hintEl.replaceChildren();
      return;
    }
    this.hintEl.innerHTML = text;
    this.hintEl.classList.remove("hidden");
  }

  addChat(name: string, text: string, self: boolean): void {
    const li = document.createElement("li");
    if (self) li.className = "self";

    const who = document.createElement("span");
    who.className = "who";
    who.textContent = `${name}: `;

    li.append(who, document.createTextNode(text));
    // A propria mensagem enviada nao conta como "nao lida": o formulario ja
    // fecha o chat ao submeter, e o eco do servidor chega logo em seguida.
    this.append(li, !self);
  }

  addSystem(message: string, level: "info" | "warn" | "error" = "info"): void {
    const li = document.createElement("li");
    li.className = level === "error" ? "system error" : "system";
    li.textContent = message;
    // Avisos do sistema nao contam como "nao lida": o badge e para mensagens
    // de outros jogadores, nao para notificacoes.
    this.append(li, false);
  }

  private append(li: HTMLLIElement, countsAsUnread: boolean): void {
    this.log.append(li);
    while (this.log.children.length > 60) this.log.firstElementChild?.remove();
    this.log.scrollTop = this.log.scrollHeight;

    // No toque o log fica escondido atras do botao de balao enquanto o chat
    // esta fechado; o desktop sempre mostra o log, entao nunca acumula.
    if (
      countsAsUnread &&
      document.body.classList.contains("touch") &&
      !document.body.classList.contains("chat-open")
    ) {
      this.setUnread(this.unread + 1);
    }
  }

  private setUnread(count: number): void {
    this.unread = count;
    if (count <= 0) {
      this.badgeEl.classList.add("hidden");
      this.badgeEl.textContent = "";
      return;
    }
    this.badgeEl.textContent = count > Hud.UNREAD_MAX ? `${Hud.UNREAD_MAX}+` : String(count);
    this.badgeEl.classList.remove("hidden");
  }
}

export function requireElement<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`elemento #${id} não encontrado no index.html`);
  return el as T;
}
